import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingUp, Calendar, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ArticleDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Failed to fetch article:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-pulse">
          <TrendingUp className="w-16 h-16 text-[#ff6b35]" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Article non trouvé</h2>
          <Button onClick={() => navigate('/')} className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-semibold">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass border-b border-[#ff6b35]/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button 
            data-testid="back-button"
            onClick={() => navigate('/')} 
            variant="ghost" 
            className="text-gray-400 hover:text-[#ff6b35] -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </header>

      {/* Article Content */}
      <article data-testid="article-detail" className="max-w-4xl mx-auto px-6 py-12">
        {/* Category Badge */}
        <div data-testid="article-category" className="inline-block px-4 py-1 rounded-full bg-[#ff6b35]/10 text-[#ff6b35] font-semibold text-sm mb-6">
          {article.category}
        </div>

        {/* Title */}
        <h1 data-testid="article-title" className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
          {article.title}
        </h1>

        {/* Meta Info */}
        <div className="flex items-center gap-6 text-gray-400 mb-8 pb-8 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span data-testid="article-author" className="text-sm">{article.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span data-testid="article-date" className="text-sm">
              {new Date(article.created_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Featured Image */}
        <div className="mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-[#ff6b35]/20 to-[#ff8555]/10 h-96 flex items-center justify-center">
          <TrendingUp className="w-32 h-32 text-[#ff6b35]" />
        </div>

        {/* Summary */}
        <div data-testid="article-summary" className="glass rounded-2xl p-6 mb-8 border-l-4 border-[#ff6b35]">
          <p className="text-lg text-gray-300 leading-relaxed">
            {article.summary}
          </p>
        </div>

        {/* Content */}
        <div data-testid="article-content" className="prose prose-invert prose-lg max-w-none">
          <div className="text-gray-300 leading-relaxed space-y-4 whitespace-pre-wrap">
            {article.content}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 glass rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">{language === 'en' ? 'Get Smart Advisory' : language === 'fr' ? 'Obtenez Conseil Intelligent' : 'Obtén Asesoramiento Inteligente'}</h3>
          <p className="text-gray-400 mb-6">{language === 'en' ? 'Receive daily analysis and manage your portfolio' : language === 'fr' ? 'Recevez analyses quotidiennes et gérez votre portfolio' : 'Recibe análisis diarios y gestiona tu portfolio'}</p>
          <Button 
            data-testid="article-cta-button"
            onClick={() => navigate('/')} 
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
          >
            {t('tryIt')}
          </Button>
        </div>
      </article>
    </div>
  );
}

export default ArticleDetail;
